import React, { useEffect, useRef, useState } from 'react';
import { getStripeClient, StripeCheckoutActions } from './stripeJs';
import { getUserFacingErrorText } from '../../../common/utilities/userFacingError';
import i18n from '../../../../locales/i18n';

// MD-I1/I3/I4 (CMP Accelerator Member Dashboard (2) ACC:1561-1584): the Pay Now card
// form is the staff Process Payment Stripe form (Custom Checkout), so the card number,
// expiry and security code stay in Stripe's fields and never reach CMP. The member's
// note is saved just before the card is submitted; the amount is the server's.

export type MemberCardFormSession = {
  checkoutSessionId: string; clientSecret: string; publishableKey: string; connectedAccountId: string;
  amountInCents: number; currency: string;
};

export const CARD_FORM_TEXT = {
  cardLabel: 'CARD INFORMATION',
  notesLabel: 'INTERNAL NOTES',
  notesPlaceholder: 'Additional details for bookkeeping...',
  confirm: 'Confirm & Record Payment',
  cancel: 'Cancel',
  incomplete: 'Complete the card number, expiry date and security code, then click Confirm & Record Payment.',
  /** Wave 3 (Oct 20 2026): en/fr text userFacingErrors.cardAmountChanged. */
  get amountMismatch() { return i18n.t('userFacingErrors.cardAmountChanged'); },
  submitted: 'Payment received. Waiting for the invoice to show it as paid…',
};

// Wave 1 (Oct 19 2026): raw Stripe.js text never shows. Stripe errors get these set texts.
export const cardFormErrorText = (chamberName?: string) => {
  const contact = chamberName?.trim() || 'your chamber';
  return {
    startFailed: `The card form did not open. Close this window and click Pay Now again. If it keeps happening, contact ${contact}.`,
    notCharged: 'Your card was not charged. Check the card number, expiry date and security code, then click Confirm & Record Payment again. If it still does not work, use a different card or call your bank.',
    confirmFailed: `We could not confirm the payment. Close this window and check if the invoice shows as paid before you try again. If it keeps happening, contact ${contact}.`,
  };
};

const NOTE_LIMIT = 1000;

export default function MemberCardPaymentForm({ start, saveNote, onPaid, onCancel, onUseHostedPage, chamberName }: {
  /** Starts (or reuses) the Stripe card form for this invoice. */
  start: () => Promise<MemberCardFormSession>;
  saveNote: (checkoutSessionId: string, note: string) => Promise<void>;
  /** Stripe confirmed the card; the invoice updates when Stripe tells CMP. */
  onPaid: () => void;
  onCancel: () => void;
  /** Event invoices keep the secure Stripe page. */
  onUseHostedPage: () => void;
  /** The member's chamber, named as the contact in error texts. */
  chamberName?: string;
}) {
  const errorText = cardFormErrorText(chamberName);
  const mountRef = useRef<HTMLDivElement>(null);
  const actions = useRef<StripeCheckoutActions>();
  const session = useRef<MemberCardFormSession>();
  const [ready, setReady] = useState(false);
  const [complete, setComplete] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    let active = true;
    let element: { destroy: () => void } | undefined;
    (async () => {
      let value: MemberCardFormSession;
      try { value = await start(); } catch (startError) {
        // The server sends event invoices to the secure Stripe page (routing only).
        const serverMessage = (startError as { graphQLErrors?: { message?: string }[] }).graphQLErrors?.[0]?.message || '';
        if (/secure Stripe page/.test(serverMessage)) { if (active) onUseHostedPage(); return; }
        if (active) setError(getUserFacingErrorText(startError, errorText.startFailed));
        return;
      }
      if (!active) return;
      session.current = value;
      const stripe = await getStripeClient(value.publishableKey, value.connectedAccountId);
      if (!active) return;
      const sdk = stripe.initCheckoutElementsSdk({ clientSecret: value.clientSecret, elementsOptions: {
        appearance: { theme: 'stripe', variables: { borderRadius: '8px', colorPrimary: '#0D8BF2', colorText: '#000000', fontFamily: '"DM Sans", sans-serif' } },
        loader: 'auto',
      } });
      const payment = sdk.createPaymentElement({ layout: 'tabs' });
      payment.on('change', event => setComplete(!!event.complete));
      if (mountRef.current) payment.mount(mountRef.current);
      element = payment;
      const loaded = await sdk.loadActions();
      if (!active) return;
      if (loaded.type !== 'success') { setError(errorText.startFailed); return; }
      const total = loaded.actions.getSession().total?.total;
      if (!total || total.minorUnitsAmount !== value.amountInCents) { setError(CARD_FORM_TEXT.amountMismatch); return; }
      actions.current = loaded.actions;
      setReady(true);
    })().catch(() => { if (active) setError(errorText.startFailed); });
    return () => { active = false; if (element) element.destroy(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirm = async () => {
    if (busy || done || !actions.current || !session.current) return;
    if (!complete) { setError(CARD_FORM_TEXT.incomplete); return; }
    setBusy(true); setError(undefined);
    try {
      if (note.trim()) await saveNote(session.current.checkoutSessionId, note.trim());
      const result = await actions.current.confirm({ redirect: 'if_required' });
      if (result.type === 'error') { setError(errorText.notCharged); setBusy(false); return; }
      setDone(true);
      onPaid();
    } catch (confirmError) {
      // A server error from the note save comes before the card is charged.
      setError(getUserFacingErrorText(confirmError, errorText.confirmFailed));
      setBusy(false);
    }
  };

  return (
    <div className="mi-card-form">
      <div className="mi-field-label">{CARD_FORM_TEXT.cardLabel}</div>
      <div ref={mountRef} className="mi-card-element" data-testid="member-card-element" />
      {!ready && !error && <p role="status" className="mi-card-loading">Loading the secure card form…</p>}
      <label className="mi-notes"><span className="mi-field-label">{CARD_FORM_TEXT.notesLabel}</span>
        <textarea value={note} maxLength={NOTE_LIMIT} disabled={busy || done} placeholder={CARD_FORM_TEXT.notesPlaceholder}
          onChange={event => setNote(event.target.value)} /></label>
      {error && <p role="alert" className="mi-card-error">{error}</p>}
      {done && <p role="status" className="mi-card-done">{CARD_FORM_TEXT.submitted}</p>}
      <div className="mi-payment-actions">
        <button type="button" className="mi-pay" disabled={!ready || busy || done} onClick={confirm}>{CARD_FORM_TEXT.confirm}</button>
        <button type="button" disabled={busy} onClick={onCancel}>{CARD_FORM_TEXT.cancel}</button>
      </div>
    </div>
  );
}
